import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaCheck } from "react-icons/fa";
import { Z_INDEX, zIndexStyle } from "@/utils/z-index";
import { chartColors, DEFAULT_ACCENT } from "@/styles/colors";

export interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  label?: string;
  error?: string | false;
  disabled?: boolean;
  showThemeColors?: boolean;
  showCustomPicker?: boolean;
}

// Theme colors - these are preset color options for the user to choose from
// Uses centralized color definitions for consistency
const themeColors = [
  { name: "Indigo", value: DEFAULT_ACCENT },
  { name: "Purple", value: chartColors.violet },
  { name: "Blue", value: chartColors.blue },
  { name: "Teal", value: chartColors.teal },
  { name: "Green", value: chartColors.green },
  { name: "Orange", value: chartColors.orange },
  { name: "Red", value: chartColors.red },
  { name: "Pink", value: chartColors.pink },
];

/**
 * Arc-style ColorPicker component
 * 
 * Features:
 * - Theme color presets
 * - Custom color picker
 * - Portal-based popover
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
  value = "var(--arc-accent)",
  onChange,
  label,
  error,
  disabled = false,
  showThemeColors = true,
  showCustomPicker = true,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const colorInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  useEffect(() => {
    if (!isPopoverOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isPopoverOpen || !popoverRef.current) return;

    const timeoutId = setTimeout(() => {
      if (!popoverRef.current) return;

      const popover = popoverRef.current;
      const rect = popover.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      setPosition((prevPos) => {
        let newX = prevPos.x;
        let newY = prevPos.y;
        let changed = false;

        if (newX + rect.width / 2 > viewportWidth) {
          newX = viewportWidth - rect.width / 2 - 10;
          changed = true;
        } else if (newX - rect.width / 2 < 10) {
          newX = rect.width / 2 + 10;
          changed = true;
        }

        if (newY + rect.height + 8 > viewportHeight) {
          newY = viewportHeight - rect.height - 8 - 10;
          changed = true;
        } else if (newY + 8 < 10) {
          newY = 10 - 8;
          changed = true;
        }

        return changed ? { x: newX, y: newY } : prevPos;
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isPopoverOpen]);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    const clickX = e.clientX;
    const clickY = e.clientY;

    setPosition({ x: clickX, y: clickY });
    setIsPopoverOpen(!isPopoverOpen);
  };

  const handleColorSelect = (color: string) => {
    setCustomColor(color);
    onChange?.(color);
    setIsPopoverOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange?.(newColor);
  };

  const isLightColor = (hex: string): boolean => {
    const cleanHex = hex.replace("#", "");
    let r = 0, g = 0, b = 0;
    if (cleanHex.length === 6) {
      r = parseInt(cleanHex.slice(0, 2), 16);
      g = parseInt(cleanHex.slice(2, 4), 16);
      b = parseInt(cleanHex.slice(4, 6), 16);
    } else if (cleanHex.length === 3) {
      r = parseInt(cleanHex[0] + cleanHex[0], 16);
      g = parseInt(cleanHex[1] + cleanHex[1], 16);
      b = parseInt(cleanHex[2] + cleanHex[2], 16);
    }
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };

  return (
    <div className={label ? "w-full" : "inline-block"}>
      {label && (
        <label
          className={`block mb-2 text-sm font-medium ${
            error ? "text-error" : "text-content-primary"
          }`}
        >
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleButtonClick}
        className={`
          flex items-center justify-center p-2 rounded-arc-md border-2 transition-all duration-arc-fast
          ${
            error
              ? "border-error"
              : isPopoverOpen
              ? "border-accent"
              : "border-arc-border"
          }
          ${
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:border-accent"
          }
          ${isPopoverOpen ? "border-accent" : ""}
        `}
      >
        <div
          className="w-8 h-8 rounded-arc-sm border-2 border-arc-border"
          style={{ backgroundColor: value }}
        />
      </button>

      {isPopoverOpen && typeof window !== "undefined"
        ? (createPortal(
            <div
              ref={popoverRef}
              className="fixed bg-surface-elevated rounded-arc-md shadow-arc-lg border border-arc-border p-3 animate-arc-fade-in"
              style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: "translate(-50%, 8px)",
                ...zIndexStyle(Z_INDEX.COLOR_PICKER),
              }}
            >
              <div className="space-y-3">
                {showThemeColors && (
                  <div className="grid grid-cols-4 gap-2">
                    {themeColors.map((color) => {
                      const isSelected = color.value === customColor;
                      const iconColor = isLightColor(color.value)
                        ? "text-content-primary"
                        : "text-white";

                      return (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => handleColorSelect(color.value)}
                          className={`
                            relative w-6 h-6 rounded border-2 transition-all duration-arc-fast
                            ${
                              isSelected
                                ? "border-accent scale-110"
                                : "border-arc-border hover:border-arc-border-strong hover:scale-105"
                            }
                          `}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        >
                          {isSelected && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FaCheck
                                className={`${iconColor} text-xs drop-shadow-lg`}
                              />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {showCustomPicker && (
                  <div className="flex items-center gap-2 pt-2 border-t border-arc-border">
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={customColor}
                      onChange={handleCustomColorChange}
                      className="w-8 h-8 rounded border border-arc-border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(newColor)) {
                          setCustomColor(newColor);
                          onChange?.(newColor);
                        }
                      }}
                      placeholder="#6366F1"
                      className="flex-1 px-2 py-1 text-xs border border-arc-border rounded-arc-sm focus:outline-none focus:border-transparent focus:ring-2 focus:ring-accent bg-surface-elevated text-content-primary"
                    />
                  </div>
                )}
              </div>
            </div>,
            document.body
          ) as React.ReactNode)
        : null}

      {error && (
        <p className="mt-1 text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default ColorPicker;



